// Live state for seat-1's run queue + driver assignments.
//
// Two pieces of mutable state:
//
//   queueIds     — Set of run IDs currently sitting in the drawer,
//                  waiting to be assigned. Seeded from PENDING_RUNS.
//
//   assignments  — Map<driverId, [{slot, runId, eta, etaMins, slotLabel}]>
//                  Same shape as the legacy SEED_ASSIGNMENTS map; this
//                  one is mutable so dispatcher actions actually move
//                  things around.
//
// Methods are designed for the two-thirds of dispatcher actions that
// matter for the demo:
//   - assignRunToDriver(runId, driverId)   — drawer → first empty slot
//   - moveSlot(fromDriverId, fromSlot, toDriverId)  — grid → grid
//   - unassignSlot(driverId, slot)          — back to drawer
//
// Plus a small "snap" handshake so cells can play a brief flash on
// receipt: lastAssigned = { driverId, slot, ts } — consumed by cells
// via a `snap` flag on the slot object.

import React, {
  createContext, useCallback, useContext, useMemo, useRef, useState,
} from 'react';
import { SEED_ASSIGNMENTS, parseShiftCode } from '../mockAssignments';
import { PENDING_RUNS, RUNS_BY_ID, BANK_BY_ID } from '../mockBank';
import { ROSTER } from '../mockRoster';
import { SEED_TEAMS } from '../mockTeams';

const RunQueueCtx = createContext(null);

// Clone the seed map deeply enough that mutations don't leak back
// into the module-scope export.
function cloneAssignments(src) {
  const out = new Map();
  src.forEach((list, driverId) => {
    out.set(driverId, list.map(item => ({ ...item })));
  });
  return out;
}

export function RunQueueProvider({ children }) {
  const [assignments, setAssignments] = useState(() => cloneAssignments(SEED_ASSIGNMENTS));
  const [queueIds, setQueueIds] = useState(() => PENDING_RUNS.map(r => r.id));

  // Drawer open/close — shared between Assignment + Timeline views via
  // sessionStorage so toggling in one view persists when the other mounts.
  const [drawerOpen, setDrawerOpen] = useState(() => {
    try { return sessionStorage.getItem('seat1.queueDrawer') !== 'closed'; }
    catch { return true; }
  });
  const toggleDrawer = useCallback(() => {
    setDrawerOpen(open => {
      const next = !open;
      try { sessionStorage.setItem('seat1.queueDrawer', next ? 'open' : 'closed'); }
      catch {}
      return next;
    });
  }, []);
  // Drawer dock side — 'left' (default) or 'right'. Same sessionStorage
  // pattern so the choice survives nav between Assignment + Timeline.
  const [drawerSide, setDrawerSide] = useState(() => {
    try { return sessionStorage.getItem('seat1.queueDrawerSide') === 'right' ? 'right' : 'left'; }
    catch { return 'left'; }
  });
  const toggleDrawerSide = useCallback(() => {
    setDrawerSide(s => {
      const next = s === 'left' ? 'right' : 'left';
      try { sessionStorage.setItem('seat1.queueDrawerSide', next); }
      catch {}
      return next;
    });
  }, []);

  // Drag preview — set while the dispatcher is dragging a RunTile from
  // the queue. The grid reads this to highlight which drivers are
  // viable candidates (green-gap fit, shift coverage, team match).
  // Cleared on dragend / drop.
  const [dragPreview, setDragPreview] = useState(null);  // { runId }
  const startDragPreview = useCallback((runId) => {
    setDragPreview({ runId });
  }, []);
  const endDragPreview = useCallback(() => setDragPreview(null), []);

  // Snap handshake — cells listen for a brief animation on assignment.
  const [snap, setSnap] = useState(null);  // { driverId, slot, ts }
  const snapTimeout = useRef(null);
  const flashSnap = useCallback((driverId, slot) => {
    setSnap({ driverId, slot, ts: Date.now() });
    clearTimeout(snapTimeout.current);
    snapTimeout.current = setTimeout(() => setSnap(null), 700);
  }, []);

  // ── Mutations ─────────────────────────────────────────────────────

  const assignRunToDriver = useCallback((runId, driverId) => {
    const run = RUNS_BY_ID.get(runId);
    if (!run) return false;
    let placedSlot = null;
    setAssignments(prev => {
      const next = new Map(prev);
      const list = (next.get(driverId) || []).slice();
      // Find the first empty slot (0..7).
      const occupied = new Set(list.map(item => item.slot));
      let slot = -1;
      for (let i = 0; i < 8; i++) {
        if (!occupied.has(i)) { slot = i; break; }
      }
      if (slot === -1) return prev;  // driver full — refuse
      placedSlot = slot;
      list.push({
        slot,
        runId,
        eta: run.inboundEta,
        etaMins: run.inboundEtaMins,
        slotLabel: run.slotLabel,
      });
      next.set(driverId, list);
      return next;
    });
    if (placedSlot !== null) {
      setQueueIds(ids => ids.filter(id => id !== runId));
      flashSnap(driverId, placedSlot);
      // Clear the drag highlight — the source RunTile unmounts as the
      // run leaves the queue, so its onDragEnd never fires.
      setDragPreview(null);
      return true;
    }
    return false;
  }, [flashSnap]);

  const unassignSlot = useCallback((driverId, slot) => {
    let removedRunId = null;
    setAssignments(prev => {
      const next = new Map(prev);
      const list = next.get(driverId) || [];
      const idx = list.findIndex(item => item.slot === slot);
      if (idx === -1) return prev;
      removedRunId = list[idx].runId;
      const newList = list.filter((_, i) => i !== idx);
      if (newList.length === 0) next.delete(driverId);
      else next.set(driverId, newList);
      return next;
    });
    if (removedRunId) {
      setQueueIds(ids => ids.includes(removedRunId) ? ids : [...ids, removedRunId]);
    }
  }, []);

  // Move a run from one driver row to another (grid → grid drag).
  // The destination just gets the next empty slot.
  const moveSlot = useCallback((fromDriverId, fromSlot, toDriverId) => {
    if (fromDriverId === toDriverId) return false;
    let movedRun = null;
    let placedSlot = null;
    setAssignments(prev => {
      const next = new Map(prev);
      const fromList = (next.get(fromDriverId) || []).slice();
      const idx = fromList.findIndex(item => item.slot === fromSlot);
      if (idx === -1) return prev;
      const item = fromList[idx];

      const toList = (next.get(toDriverId) || []).slice();
      const occupied = new Set(toList.map(x => x.slot));
      let target = -1;
      for (let i = 0; i < 8; i++) {
        if (!occupied.has(i)) { target = i; break; }
      }
      if (target === -1) return prev;  // target driver full

      const newFrom = fromList.filter((_, i) => i !== idx);
      if (newFrom.length === 0) next.delete(fromDriverId);
      else next.set(fromDriverId, newFrom);
      toList.push({ ...item, slot: target });
      next.set(toDriverId, toList);

      movedRun = item.runId;
      placedSlot = target;
      return next;
    });
    if (movedRun !== null && placedSlot !== null) {
      flashSnap(toDriverId, placedSlot);
      setDragPreview(null);
      return true;
    }
    return false;
  }, [flashSnap]);

  // ── Derived ───────────────────────────────────────────────────────

  // Group queue by inbound flight so the drawer can render one card
  // per inbound and expand into per-run children.
  const queueByInbound = useMemo(() => {
    const groups = new Map();
    queueIds.forEach(id => {
      const run = RUNS_BY_ID.get(id);
      if (!run) return;
      const list = groups.get(run.inboundId) || [];
      list.push(run);
      groups.set(run.inboundId, list);
    });
    // Sort each group by slot so Drv 1 / FWD comes before Drv 2 / AFT.
    groups.forEach(list => list.sort((a, b) => a.slot - b.slot));
    // Return as ordered array, sorted by inbound ETA.
    return Array.from(groups.entries())
      .map(([inboundId, runs]) => ({ inboundId, runs }))
      .sort((a, b) => (a.runs[0].inboundEtaMins - b.runs[0].inboundEtaMins));
  }, [queueIds]);

  const queueCount = queueIds.length;

  // ── Feasibility ──────────────────────────────────────────────────
  // For every queued run, ask: "if I gave this run to a driver as their
  // next assignment, would the gap from their last run be green, yellow,
  // or red on the dispatcher's grid?" Gap thresholds match AssignmentGrid:
  //
  //   < 50 min       → red    (won't work — driver still cleaning up)
  //   50–75 min      → yellow (tight — workable but not comfortable)
  //   > 75 min       → green  (comfortable cushion)
  //
  // A driver is eligible for a queued run when:
  //   - their shift covers the queued ETA
  //   - they have at least one empty slot
  //   - their latest assigned run BEFORE the queued ETA produces a
  //     yellow-or-better gap (red-gap drivers are not eligible)
  //
  // Run classification:
  //   ≥1 driver with GREEN gap        → comfortable, no flag
  //   0 green but ≥1 with YELLOW gap  → 'gold' (tight bench)
  //   0 green AND 0 yellow            → 'red'  (no crew available)
  //
  // Demo scope: only AM-shift drivers are considered today (the seed
  // assignments and grid render that shift). Extending to MID/PM is a
  // straight loop change.
  const feasibility = useMemo(() => {
    // Demo "now". Hardcoded to noon to match mockAssignments — the demo
    // never advances past this in any meaningful way today.
    const NOW = 12 * 60;
    const ACTIVE_WINDOW_BEFORE = 30;  // recently-started runs still count as active
    const ACTIVE_WINDOW_AFTER  = 60;  // next-hour runs count as active

    // Pre-parse shift codes once. Keep displayName + seniority so the
    // toolbar tooltips can show who's behind each count.
    const drivers = ROSTER.AM.map(d => ({
      id: d.id,
      displayName: d.displayName,
      seniority: d.seniority,
      shiftCode: d.shiftCode,
      shift: parseShiftCode(d.shiftCode),
      assigned: assignments.get(d.id) || [],
    }));

    const gapLevel = (mins) => {
      if (mins < 50)  return 'red';
      if (mins <= 75) return 'yellow';
      return 'green';
    };

    // ── Per-queued-run severity (drives tile borders + drawer signals) ──
    const perRunLevel = new Map();  // runId → 'red' | 'gold' | null
    const perRunCount = new Map();  // runId → { green, yellow }
    let tight = 0, infeasible = 0;

    for (const runId of queueIds) {
      const run = RUNS_BY_ID.get(runId);
      if (!run) continue;
      const T = run.inboundEtaMins;
      let green = 0, yellow = 0;

      for (const d of drivers) {
        if (!d.shift) continue;
        if (T < d.shift.startMin || T > d.shift.endMin) continue;
        if (d.assigned.length >= 8) continue;

        let lastEta = -Infinity;
        for (const item of d.assigned) {
          if (item.etaMins < T && item.etaMins > lastEta) lastEta = item.etaMins;
        }
        const gap = lastEta === -Infinity ? Infinity : T - lastEta;
        const lvl = gapLevel(gap);
        if (lvl === 'green')  green++;
        else if (lvl === 'yellow') yellow++;
      }

      let level = null;
      if (green === 0 && yellow === 0)      { level = 'red';  infeasible++; }
      else if (green === 0 && yellow > 0)   { level = 'gold'; tight++; }
      perRunCount.set(runId, { green, yellow });
      perRunLevel.set(runId, level);
    }

    // ── Driver-level rollups (drives the toolbar stat bar) ──
    // For each on-shift driver:
    //   active1h = has an assignment with eta in [NOW-30, NOW+60]
    //   available = on shift, has empty slot, not active1h
    // Then for each AVAILABLE driver, find their best-fit gap color across
    // any queued run. Categorize by their best option:
    //   green-best → comfortable, doesn't appear in yellow/red counts
    //   yellow-best → counts toward "Yellow Gap"
    //   red-best (or no queued run in shift) → counts toward "Red Gap"
    const queueRuns = queueIds.map(id => RUNS_BY_ID.get(id)).filter(Boolean);

    let onShift = 0;
    let active1h = 0;
    let available = 0;
    const yellowGapList = [];   // drivers whose best-fit queued run is yellow
    const redGapList    = [];   // drivers whose best-fit is red or no fit

    for (const d of drivers) {
      if (!d.shift) continue;
      const inShift = d.shift.startMin <= NOW && NOW <= d.shift.endMin;
      if (!inShift) continue;
      onShift++;

      const isActive = d.assigned.some(a =>
        a.etaMins >= NOW - ACTIVE_WINDOW_BEFORE && a.etaMins <= NOW + ACTIVE_WINDOW_AFTER
      );
      if (isActive) { active1h++; continue; }

      if (d.assigned.length >= 8) continue;
      available++;

      // Best-fit gap across the whole queue for this driver.
      let bestColor = null;  // 'green' | 'yellow' | 'red' | null
      for (const run of queueRuns) {
        const T = run.inboundEtaMins;
        if (T < d.shift.startMin || T > d.shift.endMin) continue;
        let lastEta = -Infinity;
        for (const a of d.assigned) {
          if (a.etaMins < T && a.etaMins > lastEta) lastEta = a.etaMins;
        }
        const gap = lastEta === -Infinity ? Infinity : T - lastEta;
        const c = gapLevel(gap);
        if (c === 'green') { bestColor = 'green'; break; }
        if (c === 'yellow' && bestColor !== 'yellow') bestColor = 'yellow';
        if (c === 'red' && !bestColor) bestColor = 'red';
      }
      const entry = {
        id: d.id, displayName: d.displayName,
        seniority: d.seniority, shiftCode: d.shiftCode,
      };
      if (bestColor === 'yellow') yellowGapList.push(entry);
      else if (bestColor !== 'green') redGapList.push(entry);
    }

    // Senior drivers (lower seniority number) first — matches the bid
    // order the dispatcher reads the roster in.
    yellowGapList.sort((a, b) => a.seniority - b.seniority);
    redGapList.sort((a, b) => a.seniority - b.seniority);

    return {
      perRunLevel, perRunCount, tight, infeasible,
      onShift, active1h, available,
      yellowGapDrivers: yellowGapList.length,
      redGapDrivers:    redGapList.length,
      yellowGapList, redGapList,
    };
  }, [assignments, queueIds]);

  // ── Drag candidates ─────────────────────────────────────────────
  // While a queue tile is being dragged, classify every driver as a
  // candidate signal for the grid + timeline:
  //
  //   'team'  best case — green-fit individual + on a team that can
  //           collectively cover the inbound's other runs. The whole
  //           team lights up together.
  //   'green' green-gap fit. Comfortable assignment.
  //   'red'   driver can't take this run at all. Reason is one of:
  //             - shift doesn't cover the run's ETA
  //             - shift would end before the driver could finish the
  //               run + 15 min for cleanup / clock-out
  //             - all 8 slots are full
  //             - the gap on either side (prev or next assignment) would
  //               be RED (<50 min) — assigning would create an
  //               unworkable row on the grid
  //   not in the map: yellow-tight or otherwise ambiguous — no
  //                   highlight, dispatcher can decide.
  const dragCandidates = useMemo(() => {
    if (!dragPreview) return null;
    const run = RUNS_BY_ID.get(dragPreview.runId);
    if (!run) return null;
    const inbound = BANK_BY_ID.get(run.inboundId);
    const T = run.inboundEtaMins;
    // When the driver actually frees up after the run. For normal
    // inbounds we have an explicit push time; fall back to a 45-min
    // turn estimate (originators/terminators don't carry pushMins).
    const runEndMin = inbound?.pushMins ?? (T + 45);
    const CLEAN_UP_MIN = 15;
    // Driver's shift must end no earlier than runEnd + cleanup or
    // they can't clock out cleanly.
    const requiredShiftEnd = runEndMin + CLEAN_UP_MIN;

    const result = new Map();

    for (const driver of ROSTER.AM) {
      const shift = parseShiftCode(driver.shiftCode);
      // Bad shift code or shift misalignment — driver can't take it.
      if (!shift) { result.set(driver.id, 'red'); continue; }
      if (T < shift.startMin) { result.set(driver.id, 'red'); continue; }
      if (shift.endMin < requiredShiftEnd) { result.set(driver.id, 'red'); continue; }

      const list = assignments.get(driver.id) || [];
      if (list.length >= 8) { result.set(driver.id, 'red'); continue; }

      // Both bookend gaps matter — if either would be red on the grid,
      // assigning this run would create an unworkable row.
      let lastEta = -Infinity;
      let nextEta = Infinity;
      for (const a of list) {
        if (a.etaMins < T && a.etaMins > lastEta) lastEta = a.etaMins;
        if (a.etaMins > T && a.etaMins < nextEta) nextEta = a.etaMins;
      }
      const prevGap = lastEta === -Infinity ? Infinity : T - lastEta;
      const nextGap = nextEta === Infinity ? Infinity : nextEta - T;

      if (prevGap < 50 || nextGap < 50) {
        result.set(driver.id, 'red');
      } else if (prevGap > 75 && nextGap > 75) {
        result.set(driver.id, 'green');
      }
      // yellow-tight (one or both gaps in 50–75) → no highlight
    }

    // Team upgrade pass. Only green-fit drivers are eligible — promote
    // them to 'team' when the inbound has more than one run and enough
    // of their teammates are also green-fit to cover the rest.
    const otherRunsCount = (inbound?.runs?.length ?? 1) - 1;
    if (otherRunsCount > 0) {
      const teamByDriverId = new Map();
      for (const team of SEED_TEAMS) {
        for (const id of team.driverIds) teamByDriverId.set(id, team);
      }
      for (const [driverId, fit] of result) {
        if (fit !== 'green') continue;
        const team = teamByDriverId.get(driverId);
        if (!team) continue;
        const teammates = team.driverIds.filter(id => id !== driverId);
        const availableTeammates = teammates.filter(id => result.get(id) === 'green');
        if (availableTeammates.length >= otherRunsCount) {
          result.set(driverId, 'team');
          for (const id of availableTeammates) result.set(id, 'team');
        }
      }
    }

    return result;
  }, [dragPreview, assignments]);

  const value = useMemo(() => ({
    assignments,
    queueIds,
    queueCount,
    queueByInbound,
    drawerOpen, toggleDrawer,
    drawerSide, toggleDrawerSide,
    snap,
    assignRunToDriver, unassignSlot, moveSlot,
    feasibility,
    dragPreview, startDragPreview, endDragPreview,
    dragCandidates,
  }), [
    assignments, queueIds, queueCount, queueByInbound,
    drawerOpen, toggleDrawer,
    drawerSide, toggleDrawerSide,
    snap,
    assignRunToDriver, unassignSlot, moveSlot,
    feasibility,
    dragPreview, startDragPreview, endDragPreview,
    dragCandidates,
  ]);

  return (
    <RunQueueCtx.Provider value={value}>
      {children}
    </RunQueueCtx.Provider>
  );
}

export function useRunQueue() {
  const ctx = useContext(RunQueueCtx);
  if (!ctx) throw new Error('useRunQueue must be used inside <RunQueueProvider>');
  return ctx;
}

// MIME types for HTML5 drag-and-drop. Source-of-truth so consumers can
// import instead of re-typing string keys.
export const DND = {
  // Drawer → grid. Payload: runId.
  RUN_FROM_QUEUE: 'application/x-seat1-queue-run',
  // Grid → grid (move between drivers). Payload: `${driverId}|${slot}`.
  RUN_FROM_GRID:  'application/x-seat1-grid-run',
};
